import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useLeads } from "@/hooks/useLeads";
import { useBankers } from "@/hooks/useBankers";
import { useAppConfig } from "@/hooks/useAppConfig";
import { useFileUpload } from "@/hooks/useFileUpload";
import { FileUploadSection } from "@/components/common/FileUploadSection";
import { AssociatedTasks } from "@/components/dashboard/AssociatedTasks";
import { AssociatedLeads } from "@/components/dashboard/AssociatedLeads";
import type { SMContact, Lead, BankerDocument } from "@/types";
import { LOAN_STATUS_CONFIG, getLegacyCustomerName } from "@/types";
import { INDIAN_CITIES } from "@/lib/constants";
import {
  ArrowLeft,
  Phone,
  MessageCircle,
  Mail,
  Star,
  StarOff,
  Edit2,
  Building2,
  User,
  AlertTriangle,
  FileText,
  Loader2,
  X,
  CreditCard,
  Eye,
  Download,
  AlertCircle,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  MapPin,
  Navigation,
  Map,
} from "lucide-react";
import { MapLocationPicker } from "@/components/common/MapLocationPicker";

import { toast } from "sonner";
import { format } from "date-fns";
import { ImageViewer } from "@/components/common/ImageViewer";
import { usePinchZoom } from "@/hooks/usePinchZoom";

const DESIGNATION_OPTIONS = [
  "National Head",
  "Zonal Manager",
  "Regional Manager",
  "Area Sales Manager",
  "Sales Manager",
  "Other",
];

export default function SMDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { leads } = useLeads();
  const {
    bankers,
    loading,
    updateBanker,
    toggleFavorite,
    isFavorite,
    uploadDocument,
    deleteDocument,
  } = useBankers();
  const { config } = useAppConfig();

  const cityOptions = INDIAN_CITIES.map((city) => ({
    value: city,
    label: city,
  }));
  const bankOptions = config.banks.map((bank) => ({
    value: bank,
    label: bank,
  }));

  const smContact = bankers.find((b) => b.id === id);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<SMContact>>({});
  const [editCustomDesignation, setEditCustomDesignation] = useState("");
  const [isOtherDesignation, setIsOtherDesignation] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMapPickerOpen, setEditMapPickerOpen] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [viewingDocument, setViewingDocument] = useState<BankerDocument | null>(
    null,
  );
  const [docIframeLoading, setDocIframeLoading] = useState(true);
  const [docIframeError, setDocIframeError] = useState(false);
  const {
    zoom: docZoomLevel,
    setZoom: setDocZoomLevel,
    zoomIn: docZoomIn,
    zoomOut: docZoomOut,
    resetZoom: docResetZoom,
    touchHandlers: docTouchHandlers,
  } = usePinchZoom({ minZoom: 0.25, maxZoom: 5 });

  const fileUploadManager = useFileUpload({
    maxSizeMB: config.maxFileUploadSizeMB,
    maxFiles: config.maxFileUploads,
    allowedFileTypes: config.allowedFileTypes,
    uploadMode: "deferred",
  });

  const fileManagerFiles = fileUploadManager.pendingFiles;
  const fileManagerReset = fileUploadManager.resetPending;

  useEffect(() => {
    if (smContact) {
      setEditForm({
        ...smContact,
        // Store only the last 10 digits in the form (UI shows +91 prefix separately)
        phone: (smContact.phone || "").replace(/\D/g, "").slice(-10),
      });
      // If the saved designation is not in the standard list, it was a custom "Other" value
      if (
        smContact.designation &&
        !DESIGNATION_OPTIONS.includes(smContact.designation)
      ) {
        setIsOtherDesignation(true);
        setEditCustomDesignation(smContact.designation);
      } else {
        setIsOtherDesignation(false);
        setEditCustomDesignation("");
      }
    }
  }, [smContact]);

  if (loading) {
    return (
      <AppLayout>
        <div className="p-4 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  if (!smContact) {
    return (
      <AppLayout>
        <div className="p-4 text-center">
          <p className="text-muted-foreground">Banker Contact not found</p>
          <Button variant="link" onClick={() => navigate("/tools/sm-contact")}>
            Go back
          </Button>
        </div>
      </AppLayout>
    );
  }

  const handleToggleFavorite = async () => {
    try {
      await toggleFavorite(smContact.id);
      toast.success(
        isFavorite(smContact.id)
          ? "Removed from favorites"
          : "Added to favorites",
      );
    } catch (error) {
      toast.error("Failed to update favorite");
    }
  };

  const handleProductToggle = (productValue: string) => {
    setEditForm((prev) => ({
      ...prev,
      products: (prev.products || []).includes(productValue)
        ? (prev.products || []).filter((p) => p !== productValue)
        : [...(prev.products || []), productValue],
    }));
  };

  const handleRemoveExistingDocument = async (doc: BankerDocument) => {
    if (!smContact?.id) return;

    try {
      // Delete from Firebase Storage if URL exists
      if (doc.url) {
        await deleteDocument(doc.url);
      }

      // Update banker's documents array in Firestore
      const updatedDocuments =
        smContact.documents?.filter((d) => d.id !== doc.id) || [];
      await updateBanker(smContact.id, { documents: updatedDocuments });

      toast.success("Document deleted");
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document");
    }
  };

  const handleSaveEdit = async () => {
    if (!smContact?.id) return;

    if (!editForm.name?.trim() || !editForm.bank || !editForm.phone?.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (editForm.phone.replace(/\D/g, "").length !== 10) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload new documents first
      const uploadedDocs: BankerDocument[] = [];
      if (fileManagerFiles.length > 0) {
        toast.info(
          `Uploading ${fileManagerFiles.length} file${fileManagerFiles.length > 1 ? "s" : ""}...`,
        );

        const uploadPromises = fileManagerFiles.map((file) =>
          uploadDocument(file, smContact.id),
        );
        const results = await Promise.all(uploadPromises);
        uploadedDocs.push(...results);

        toast.success(
          `Uploaded ${uploadedDocs.length} file${uploadedDocs.length > 1 ? "s" : ""} successfully`,
        );
      }

      // Combine existing documents with newly uploaded ones
      const allDocuments = [...(smContact.documents || []), ...uploadedDocs];

      // Prepare update data with only defined values
      const updateData: Record<string, unknown> = {
        name: editForm.name!,
        bank: editForm.bank!,
        phone: editForm.phone!,
        products: editForm.products || [],
        documents: allDocuments,
      };

      // Only include optional fields if they have values
      if (editForm.email?.trim()) {
        updateData.email = editForm.email.trim();
      }
      if (editForm.designation?.trim()) {
        updateData.designation = editForm.designation.trim();
      }
      if (editForm.remark?.trim()) {
        updateData.remark = editForm.remark.trim();
      }
      if (editForm.region?.trim()) {
        updateData.region = editForm.region.trim();
      }
      if (editForm.location) {
        updateData.location = editForm.location;
      }

      // Update banker details
      await updateBanker(smContact.id, updateData);

      setIsEditing(false);
      fileManagerReset();
      toast.success("Banker details updated");
    } catch (error: unknown) {
      const msg =
        error instanceof Error ? error.message : "Failed to update banker";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenReport = () => {
    setReportReason(smContact?.reportReason || "");
    setReportDialogOpen(true);
  };

  const handleSubmitReport = async () => {
    if (!reportReason.trim()) {
      toast.error("Please enter a report reason");
      return;
    }

    if (!smContact?.id) return;

    setIsSubmitting(true);
    try {
      await updateBanker(smContact.id, {
        isReported: true,
        reportReason,
      });
      toast.success("Banker reported successfully");
      setReportDialogOpen(false);
    } catch (error) {
      toast.error("Failed to report banker");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout>
      <div
        className="flex flex-col md:h-screen md:h-dvh"
        style={{ height: "calc(var(--viewport-height) - 80px)" }}
      >
        {/* Header - Sticky */}
        <div className="sticky top-0 z-10 bg-background border-b">
          <div className="max-w-4xl mx-auto p-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/tools/sm-contact")}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-lg md:text-xl font-bold truncate">
                    {smContact.name}
                  </h1>
                  {smContact.isReported && (
                    <Badge variant="destructive" className="text-xs">
                      Reported
                    </Badge>
                  )}
                </div>
                <Badge variant="secondary" className="text-xs">
                  {smContact.bank}
                </Badge>
              </div>
              <Button variant="ghost" size="icon" onClick={handleOpenReport}>
                <AlertTriangle className="h-5 w-5 text-warning" />
              </Button>
              <button onClick={handleToggleFavorite}>
                {isFavorite(smContact.id) ? (
                  <Star className="h-6 w-6 text-gold fill-gold" />
                ) : (
                  <StarOff className="h-6 w-6 text-muted-foreground" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto pb-24 md:pb-8">
            <div className="space-y-4">
              {/* Report Banner if reported */}
              {smContact.isReported && smContact.reportReason && (
                <Card className="bg-warning/10 border-warning/20">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-warning shrink-0" />
                      <div>
                        <p className="font-medium text-warning">Reported</p>
                        <p className="text-sm text-muted-foreground">
                          {smContact.reportReason}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Contact Details */}
              <Card>
                <CardHeader className="pb-2 flex-row items-center justify-between">
                  <CardTitle className="text-base">Contact Details</CardTitle>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setIsEditing(true)}
                    className="h-8 w-8 p-0 rounded-full"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{smContact.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {smContact.bank} • {smContact.region}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{smContact.phone}</span>
                  </div>

                  {smContact.email && (
                    <div className="flex items-center gap-3 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{smContact.email}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-3 text-sm">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>{smContact.region}</span>
                  </div>

                  {smContact.designation && (
                    <div className="flex items-center gap-3 text-sm">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{smContact.designation}</span>
                    </div>
                  )}

                  {/* Remark */}
                  {smContact.remark && (
                    <div className="flex items-start gap-3 text-sm pt-2 border-t">
                      <FileText className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <p className="text-muted-foreground">
                        {smContact.remark}
                      </p>
                    </div>
                  )}

                  {/* Location */}
                  {smContact.location && (
                    <div className="flex items-center gap-3 text-sm pt-2 border-t">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <button
                        className="text-primary underline-offset-2 hover:underline text-left"
                        onClick={() =>
                          window.open(
                            `https://www.google.com/maps/dir/?api=1&destination=${smContact.location!.lat},${smContact.location!.lng}`,
                            "_blank",
                          )
                        }
                      >
                        📍 {smContact.location.lat.toFixed(6)},{" "}
                        {smContact.location.lng.toFixed(6)}
                      </button>
                    </div>
                  )}

                  {/* Products */}
                  {smContact.products && smContact.products.length > 0 && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-2">
                        Products
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {smContact.products.map((p) => (
                          <Badge key={p} variant="accent" className="text-xs">
                            {p}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quick Actions */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-8"
                      onClick={() => window.open(`tel:${smContact.phone}`)}
                    >
                      <Phone className="h-4 w-4 mr-1" /> Call
                    </Button>
                    <Button
                      size="sm"
                      variant="success"
                      className="flex-1 h-8"
                      onClick={() => {
                        const n = smContact.phone.replace(/\D/g, "");
                        window.open(
                          `https://wa.me/${n.startsWith("91") && n.length > 10 ? n : "91" + n}`,
                        );
                      }}
                    >
                      <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="shrink-0 h-8 px-3"
                      onClick={() => {
                        if (smContact.location) {
                          window.open(
                            `https://www.google.com/maps?q=${smContact.location.lat},${smContact.location.lng}`,
                          );
                        } else {
                          window.open(
                            `https://www.google.com/maps/search/${encodeURIComponent(smContact.bank + " " + smContact.region)}`,
                          );
                        }
                      }}
                    >
                      <Navigation className="h-4 w-4" />
                    </Button>
                    {smContact.email && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8"
                        onClick={() => window.open(`mailto:${smContact.email}`)}
                      >
                        <Mail className="h-4 w-4 mr-1" /> Email
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Documents / Visiting Cards */}
              {smContact.documents && smContact.documents.length > 0 && (
                <Card id="documents-section">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      Documents / Visiting Card ({smContact.documents.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {smContact.documents
                        .filter((d) => d?.type && d?.url && d?.id)
                        .map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/50 group"
                          >
                            <div
                              className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
                              onClick={() => {
                                setViewingDocument(doc);
                                setDocIframeLoading(true);
                                setDocIframeError(false);
                              }}
                            >
                              <div className="h-10 w-10 rounded bg-background flex items-center justify-center shrink-0">
                                {doc.type.startsWith("image/") ? (
                                  <img
                                    src={doc.url}
                                    alt={doc.name}
                                    className="h-10 w-10 rounded object-cover"
                                  />
                                ) : (
                                  <FileText className="h-5 w-5 text-muted-foreground" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {doc.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {doc.type.startsWith("image/")
                                    ? "Image"
                                    : doc.type.includes("pdf")
                                      ? "PDF"
                                      : "Document"}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewingDocument(doc);
                                  setDocIframeLoading(true);
                                  setDocIframeError(false);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <a
                                href={doc.url}
                                download={doc.name}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-accent/10 transition-colors"
                              >
                                <Download className="h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Associated Reminders */}
              <AssociatedTasks entityType="banker" entityId={smContact.id} />

              {/* Associated Leads */}
              <AssociatedLeads entityType="banker" entityId={smContact.id} />
            </div>
          </div>
        </div>

        {/* Edit Sheet */}
        <Sheet open={isEditing} onOpenChange={setIsEditing}>
          <SheetContent
            side="bottom"
            className="h-[90vh] rounded-t-3xl flex flex-col"
          >
            <SheetHeader className="pb-4 flex-shrink-0">
              <SheetTitle className="text-xl font-bold">
                Edit Banker Details
              </SheetTitle>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6">
              <div className="space-y-5 pb-8">
                {/* SECTION: Basic Information */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 border-b border-green-100 pb-2">
                    <User className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-sm text-green-800">
                      Basic Information
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Name *</Label>
                    <Input
                      value={editForm.name || ""}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, name: e.target.value }))
                      }
                      placeholder="Ex - Rajesh Kumar"
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Bank *</Label>
                    <SearchableSelect
                      options={bankOptions}
                      value={editForm.bank || ""}
                      onValueChange={(value) =>
                        setEditForm((p) => ({ ...p, bank: value }))
                      }
                      placeholder="Search bank..."
                      className="h-12"
                    />
                  </div>
                </div>

                {/* SECTION: Contact Details */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 border-b border-green-100 pb-2">
                    <Phone className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-sm text-green-800">
                      Contact Details
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Phone *</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">
                        +91
                      </span>
                      <Input
                        value={editForm.phone || ""}
                        onChange={(e) =>
                          setEditForm((p) => ({
                            ...p,
                            phone: e.target.value.replace(/\D/g, "").slice(-10),
                          }))
                        }
                        placeholder="9876543210"
                        maxLength={10}
                        className="h-12 rounded-l-none"
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Email</Label>
                    <Input
                      type="email"
                      value={editForm.email || ""}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, email: e.target.value }))
                      }
                      placeholder="Ex - name@email.com"
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Region</Label>
                    <SearchableSelect
                      options={cityOptions}
                      value={editForm.region || ""}
                      onValueChange={(value) =>
                        setEditForm((p) => ({ ...p, region: value }))
                      }
                      placeholder="Search city..."
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Designation</Label>
                    <Select
                      value={
                        isOtherDesignation
                          ? "Other"
                          : DESIGNATION_OPTIONS.includes(
                                editForm.designation || "",
                              )
                            ? editForm.designation
                            : editForm.designation
                              ? "Other"
                              : ""
                      }
                      onValueChange={(value) => {
                        if (value === "Other") {
                          setIsOtherDesignation(true);
                          setEditCustomDesignation("");
                          setEditForm((p) => ({ ...p, designation: "" }));
                        } else {
                          setIsOtherDesignation(false);
                          setEditCustomDesignation("");
                          setEditForm((p) => ({ ...p, designation: value }));
                        }
                      }}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select designation" />
                      </SelectTrigger>
                      <SelectContent>
                        {DESIGNATION_OPTIONS.map((d) => (
                          <SelectItem key={d} value={d}>
                            {d}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {isOtherDesignation && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Enter Designation *
                      </Label>
                      <Input
                        value={editCustomDesignation}
                        onChange={(e) => {
                          setEditCustomDesignation(e.target.value);
                          setEditForm((p) => ({
                            ...p,
                            designation: e.target.value,
                          }));
                        }}
                        placeholder="e.g. Branch Manager, Credit Head..."
                        className="h-12 placeholder:text-muted-foreground/50"
                        autoFocus
                      />
                    </div>
                  )}
                </div>

                {/* SECTION: Products */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 border-b border-green-100 pb-2">
                    <CreditCard className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-sm text-green-800">
                      Products
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      {config.products.map((product) => (
                        <div
                          key={product}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={`edit-${product}`}
                            checked={(editForm.products || []).includes(
                              product,
                            )}
                            onCheckedChange={() => handleProductToggle(product)}
                          />
                          <label
                            htmlFor={`edit-${product}`}
                            className="text-sm cursor-pointer"
                          >
                            {product}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* SECTION: Notes */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 border-b border-green-100 pb-2">
                    <FileText className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-sm text-green-800">
                      Notes
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <Textarea
                      value={editForm.remark || ""}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, remark: e.target.value }))
                      }
                      placeholder="Any notes about this SM..."
                      rows={2}
                      className="resize-none"
                    />
                  </div>
                </div>

                {/* SECTION: Location */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 border-b border-green-100 pb-2">
                    <MapPin className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-sm text-green-800">
                      Location
                    </h3>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 h-12"
                        onClick={() => setEditMapPickerOpen(true)}
                      >
                        <Map className="h-4 w-4 mr-2 shrink-0" />
                        {editForm.location
                          ? "Location Set ✓ — Change"
                          : "Update Location on Map"}
                      </Button>
                    </div>
                    {editForm.location && (
                      <p className="text-xs text-muted-foreground pl-1">
                        📍 {editForm.location.lat.toFixed(6)},{" "}
                        {editForm.location.lng.toFixed(6)}
                      </p>
                    )}
                  </div>
                </div>

                {/* SECTION: Documents */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 border-b border-green-100 pb-2">
                    <FileText className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-sm text-green-800">
                      Documents
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {/* Existing Documents */}
                    {smContact.documents && smContact.documents.length > 0 && (
                      <div className="space-y-2 mb-2">
                        <p className="text-xs text-muted-foreground">
                          Existing Documents
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {smContact.documents
                            .filter((doc) => doc?.type && doc?.url && doc?.id)
                            .map((doc, index) =>
                              doc.type.startsWith("image/") ? (
                                <div
                                  key={doc.id}
                                  className="relative aspect-square rounded-lg overflow-hidden"
                                >
                                  <img
                                    src={doc.url}
                                    alt={doc.name}
                                    className="w-full h-full object-cover cursor-pointer"
                                    onClick={() => {
                                      setSelectedImageIndex(index);
                                      setImageViewerOpen(true);
                                    }}
                                  />
                                  <button
                                    onClick={() =>
                                      handleRemoveExistingDocument(doc)
                                    }
                                    className="absolute top-1 right-1 bg-destructive text-destructive-foreground p-1 rounded-full"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ) : (
                                <Badge
                                  key={doc.id}
                                  variant="secondary"
                                  className="flex items-center gap-1.5 py-1.5 px-2.5"
                                >
                                  <FileText className="h-3.5 w-3.5" />
                                  <span className="max-w-[100px] truncate text-xs">
                                    {doc.name}
                                  </span>
                                  <button
                                    onClick={() =>
                                      handleRemoveExistingDocument(doc)
                                    }
                                    className="ml-0.5 hover:text-destructive"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ),
                            )}
                        </div>
                      </div>
                    )}

                    <FileUploadSection
                      files={fileManagerFiles}
                      onCameraCapture={fileUploadManager.handleCameraCapture}
                      onFileUpload={fileUploadManager.handleFileUpload}
                      onRemoveFile={fileUploadManager.handleRemovePendingFile}
                      onImageClick={fileUploadManager.openImageViewer}
                      imageViewerState={{
                        isOpen: fileUploadManager.imageViewerState.isOpen,
                        images: fileUploadManager.imageViewerState.images,
                        currentIndex:
                          fileUploadManager.imageViewerState.currentIndex,
                        setCurrentIndex:
                          fileUploadManager.imageViewerState.setCurrentIndex,
                        onClose: fileUploadManager.closeImageViewer,
                      }}
                      label="New Documents to Upload"
                      helpText="Photos, documents, PDFs, etc."
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSaveEdit}
                  disabled={isSubmitting}
                  className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        <MapLocationPicker
          open={editMapPickerOpen}
          onOpenChange={setEditMapPickerOpen}
          initialLocation={editForm.location}
          onLocationSelect={(location) => {
            setEditForm((p) => ({ ...p, location }));
            toast.success("Location selected from map");
          }}
        />

        {/* Report Dialog */}
        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogContent className="max-w-md mx-4 rounded-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-warning" />
                Report SM
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Report Reason *</Label>
                <Textarea
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  placeholder="e.g., SM left job, Not responding, Incorrect information..."
                  rows={3}
                  className="rounded-xl"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setReportDialogOpen(false)}
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleSubmitReport}
                  disabled={isSubmitting}
                  className="flex-1 rounded-xl"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Report"
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Image Viewer for existing documents */}
        {smContact.documents && smContact.documents.length > 0 && (
          <ImageViewer
            images={smContact.documents
              .filter((d) => d?.type?.startsWith("image/") && d?.url)
              .map((d) => d.url!)}
            currentIndex={selectedImageIndex}
            isOpen={imageViewerOpen}
            onClose={() => setImageViewerOpen(false)}
            onIndexChange={setSelectedImageIndex}
          />
        )}

        {/* Rich Document Viewer Dialog */}
        <Dialog
          open={viewingDocument !== null}
          onOpenChange={(open) => {
            if (!open) {
              setViewingDocument(null);
              setDocIframeError(false);
              setDocIframeLoading(true);
              docResetZoom();
            }
          }}
        >
          <DialogContent className="max-w-5xl w-[calc(100vw-1rem)] max-h-[95dvh] p-0 gap-0 overflow-hidden [&>.absolute]:hidden">
            {viewingDocument && (
              <>
                {/* Viewer Header */}
                <div className="flex items-center gap-2 px-3 py-2.5 border-b bg-card/95 backdrop-blur-sm">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <h3 className="font-semibold text-sm flex-1 min-w-0 truncate">
                    {viewingDocument.name}
                  </h3>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Zoom Controls */}
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          setDocZoomLevel((prev) => Math.max(0.25, prev - 0.25))
                        }
                        title="Zoom Out"
                      >
                        <ZoomOut className="h-3.5 w-3.5" />
                      </Button>
                      <span className="text-xs font-medium min-w-[3rem] text-center">
                        {Math.round(docZoomLevel * 100)}%
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          setDocZoomLevel((prev) => Math.min(3, prev + 0.25))
                        }
                        title="Zoom In"
                      >
                        <ZoomIn className="h-3.5 w-3.5" />
                      </Button>
                      {docZoomLevel !== 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setDocZoomLevel(1)}
                          title="Reset Zoom"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    <a
                      href={viewingDocument.url}
                      download={viewingDocument.name}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 px-2"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setViewingDocument(null);
                        setDocIframeError(false);
                        setDocIframeLoading(true);
                        docResetZoom();
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Viewer Content */}
                <div className="p-2 overflow-auto max-h-[calc(95dvh-52px)] flex items-center justify-center">
                  {viewingDocument.type.startsWith("image/") ? (
                    <div
                      className="w-full flex items-center justify-center bg-black/5 rounded-lg overflow-hidden"
                      {...docTouchHandlers}
                      style={{ touchAction: "none" }}
                    >
                      <img
                        src={viewingDocument.url}
                        alt={viewingDocument.name}
                        className="w-full max-h-[calc(95dvh-60px)] object-contain transition-transform duration-200"
                        style={{
                          transform: `scale(${docZoomLevel})`,
                          transformOrigin: "center center",
                        }}
                      />
                    </div>
                  ) : viewingDocument.type.includes("pdf") ? (
                    <div className="w-full h-[70vh] rounded-lg overflow-hidden relative">
                      {docIframeLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            <p className="text-sm text-muted-foreground">
                              Loading PDF...
                            </p>
                          </div>
                        </div>
                      )}
                      <iframe
                        src={`${viewingDocument.url}#toolbar=1&navpanes=1&scrollbar=1`}
                        className="w-full h-full border-0"
                        title={viewingDocument.name}
                        onLoad={() => setDocIframeLoading(false)}
                        onError={() => {
                          setDocIframeError(true);
                          setDocIframeLoading(false);
                        }}
                      />
                    </div>
                  ) : docIframeError ? (
                    <div className="text-center py-12">
                      <AlertCircle className="h-10 w-10 text-warning mx-auto mb-3" />
                      <h4 className="font-medium mb-2">
                        Unable to preview document
                      </h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Try opening or downloading the file.
                      </p>
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          onClick={() =>
                            window.open(viewingDocument.url, "_blank")
                          }
                        >
                          <ExternalLink className="h-4 w-4 mr-2" /> Open
                          Externally
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-[70vh] rounded-lg overflow-hidden relative">
                      {docIframeLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            <p className="text-sm text-muted-foreground">
                              Loading document...
                            </p>
                          </div>
                        </div>
                      )}
                      <iframe
                        src={viewingDocument.url}
                        className="w-full h-full border-0"
                        title={viewingDocument.name}
                        onLoad={() => setDocIframeLoading(false)}
                        onError={() => {
                          setDocIframeError(true);
                          setDocIframeLoading(false);
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
